import { copiesOnPets, petGearInventory, buildPetEquipment } from './petEquipmentService.ts';
import { hotlistAllocation } from '../rules/hotlist.ts';
import type { Character } from '../domain/character.ts';
import { validateCharacter } from '../domain/validateCharacter.ts';
import { validateInventoryAgainstCatalogue } from '../rules/inventory.ts';
import type { InventoryCatalogue } from '../rules/inventory.ts';

/**
 * The one check a crawler passes before it becomes mechanically ACTIVE.
 *
 * There are two validators, and neither alone is enough:
 *
 *  - `validateCharacter` is catalogue-INDEPENDENT on purpose. The domain layer
 *    must not reach for a global registry, and a crawler owning an Item this
 *    installation does not have is a legitimate state - an import from a
 *    machine with content we lack keeps its IDs rather than losing them.
 *  - `validateInventoryAgainstCatalogue` needs the catalogue, because Gear Slot
 *    capacity cannot be checked without knowing what the Items are.
 *
 * Equipped Items feed Effects (RULE DCC-INV-002), so an impossible layout is
 * not a cosmetic problem: two Torso items worn at once contribute both bonuses.
 * That makes this a boundary rather than a nicety, and it has to sit wherever a
 * crawler crosses INTO the running application:
 *
 *  - portable import, before anything is written;
 *  - opening a crawler to play, before its Effects are resolved.
 *
 * A UI control is not a boundary. By the time a button could be disabled, an
 * import or a hand-edited file has already had its chance.
 *
 * RULE DCC-INV-012.
 */

export interface IntegrityReport {
  ok: boolean;
  problems: string[];
}

export function checkCharacterIntegrity(
  character: Character,
  catalogue: InventoryCatalogue,
): IntegrityReport {
  const problems: string[] = [];

  const base = validateCharacter(character);
  if (!base.valid) problems.push(...base.errors);

  // Note what this does NOT complain about: an unknown item the crawler merely
  // OWNS. That data is preserved, because losing a player's item because we
  // lack its definition would be worse than not knowing what it is. Only
  // wearing one is impossible, because only worn items do anything.
  for (const problem of validateInventoryAgainstCatalogue(character.inventory, catalogue)) {
    problems.push(`Inventory: ${problem.itemId} ${problem.message}`);
  }

  for (const owned of character.inventory) {
    if (copiesOnPets(character, owned.itemId) > 0 && owned.equipped + owned.held + hotlistAllocation(character.hotlist, owned.itemId) + copiesOnPets(character, owned.itemId) > owned.quantity) problems.push(`Inventory: ${owned.itemId} has more copies allocated than owned.`);
    if (owned.equipped && catalogue.lookupItem(owned.itemId)?.petGear) problems.push(`Inventory: ${owned.itemId} is pet-only equipment.`);
  }
  for (const pet of character.companions) {
    const equipment = buildPetEquipment(character, pet, catalogue.lookupItem);
    if (Object.values(equipment.enhancedStats).some((value) => !Number.isInteger(value) || value < 1)) problems.push(`${pet.name}: equipment takes a Stat below 1; ask the GM to correct it.`);
    const known = petGearInventory(pet).filter((entry) => catalogue.lookupItem(entry.itemId)?.petGear === true);
    for (const entry of known) if (!character.inventory.some((owned) => owned.itemId === entry.itemId && owned.quantity >= entry.quantity)) problems.push(`Pet equipment: ${pet.name}: ${entry.itemId} is not backed by enough copies in the owner's Inventory.`);
    for (const problem of validateInventoryAgainstCatalogue(known, catalogue)) problems.push(`Pet equipment: ${pet.name}: ${problem.message}`);
  }
  return { ok: problems.length === 0, problems };
}

/** Convenience for callers that want to fail loudly rather than branch. */
export function assertCharacterIntegrity(
  character: Character,
  catalogue: InventoryCatalogue,
): void {
  const report = checkCharacterIntegrity(character, catalogue);
  if (!report.ok) {
    throw new Error(
      `${character.name} cannot be used as saved:\n${report.problems.map((p) => `- ${p}`).join('\n')}`,
    );
  }
}
